import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Link,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import TuneIcon from "@mui/icons-material/Tune";
import { useAuth } from "../features/auth/hooks/useAuth";
import { useOrganization } from "../features/auth/hooks/useOrganization";
import { ROUTES } from "../constants";

const DRAWER_WIDTH = 260;

function AppLayout({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { signOut, userData, organizationId, isLoading } = useAuth();
  const { organization } = useOrganization(organizationId);
  const { pathname } = useLocation();
  const appTitle = organization?.name || "Mighty45";

  useEffect(() => {
    document.title = organization?.name ? `${organization.name} - Client` : "Mighty45 Client";
  }, [organization?.name]);

  const displayName =
    userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`.trim()
      : userData?.displayName || userData?.email || "Client";

  const handleSignOut = () => {
    setDrawerOpen(false);
    signOut();
    window.location.href = ROUTES.SIGN_IN;
  };

  const closeDrawer = () => setDrawerOpen(false);

  const navLinks = [
    { to: ROUTES.DASHBOARD, label: "Dashboard", icon: <DashboardIcon /> },
    { to: ROUTES.TRAINING_PLAN, label: "Training Plan", icon: <FitnessCenterIcon /> },
    { to: ROUTES.WORKOUT_TOOLS, label: "Workout tools", icon: <TuneIcon /> },
  ];

  const drawerContent = (
    <Box sx={{ width: DRAWER_WIDTH, height: "100%", display: "flex", flexDirection: "column" }}>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          {appTitle}
        </Typography>
      </Toolbar>
      <Divider />
      <List component="nav" sx={{ flex: 1, px: 1 }}>
        {navLinks.map(({ to, label, icon }) => (
          <ListItem key={to} disablePadding>
            <ListItemButton
              component={NavLink}
              to={to}
              onClick={closeDrawer}
              selected={pathname === to}
              sx={{
                borderRadius: 1,
                "&.Mui-selected": { bgcolor: "action.selected" },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>{icon}</ListItemIcon>
              <ListItemText primary={label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      {!isLoading && (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: "secondary.main" }}>
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {displayName}
            </Typography>
          </Box>
          <Button
            fullWidth
            color="inherit"
            variant="outlined"
            size="medium"
            startIcon={<LogoutIcon />}
            onClick={handleSignOut}
            sx={{ justifyContent: "flex-start", borderColor: "divider", color: "text.primary" }}
          >
            Sign out
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar position="static" color="primary">
        <Toolbar sx={{ gap: 1 }}>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="Open menu"
              edge="start"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {appTitle}
          </Typography>
          {!isMobile && (
            <Box
              component="nav"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {navLinks.map(({ to, label }) => (
                <Link
                  key={to}
                  component={NavLink}
                  to={to}
                  color="inherit"
                  underline="none"
                  sx={{
                    fontWeight: pathname === to ? 700 : 400,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    "&:hover": { opacity: 0.9, bgcolor: "action.hover" },
                  }}
                >
                  {label}
                </Link>
              ))}
            </Box>
          )}
          {!isMobile && !isLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: "secondary.main" }}>
                {displayName.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="body2" sx={{ display: { xs: "none", sm: "block" } }}>
                {displayName}
              </Typography>
              <Button
                color="inherit"
                startIcon={<LogoutIcon />}
                onClick={handleSignOut}
                size="small"
              >
                Sign out
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="left"
        open={drawerOpen}
        onClose={closeDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: DRAWER_WIDTH },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2 },
          bgcolor: "background.default",
        }}
      >
        {children || <Outlet />}
      </Box>
    </Box>
  );
}

export default AppLayout;
